import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AppThunk, AppDispatch } from '../appStore'
import api from '../api';
import { ILoginResult } from '../api';
import IAuthState from '../../interfaces/IAuthState'
import { parseJwt } from '../../common/helpers';
import { RootState } from '../rootReducer';
import IUserData from '../../interfaces/IUserData';
const initialState: IAuthState = {
    isAuthenticated: false,
    token: '',
    userName: '',
    data: null,
    sessionExpired: false,
    vehicleSessionId: null,
    vehicleNumber: null,
    shiftType: null,
    clusterId: null,
    appId: null,
    hasTransportLocations: false,
    hasAgedCareLocations: false,
}

const authSlice = createSlice({
    name: 'authStore',
    initialState,
    reducers: {
        login(state: IAuthState, action: PayloadAction<ILoginResult>) {
            if (action.payload.token && action.payload.token !== '' && action.payload.token.length > 10) {
                state.token = action.payload.token;
                state.isAuthenticated = true;
                state.sessionExpired = false;
                const tokenObject = parseJwt(action.payload.token ?? '');
                state.data = {
                    givenName: String(tokenObject.given_name),
                    surName: String(tokenObject.family_name),
                    email: String(tokenObject.email),
                    role: String(tokenObject.role),
                    IsAdmin: tokenObject.IsAdmin === "True",
                    IsSMAUser: tokenObject.IsSMAUser === "True",
                    exp: tokenObject.exp,
                    location: String(tokenObject.location ?? tokenObject.Location),
                    ward: String(tokenObject.ward ?? tokenObject.Ward),
                    isSharedUser: tokenObject.isSharedUser === "True",
                    lastLogin: String(tokenObject.LastLoginDate),
                    lastLoginUTC: String(tokenObject.LastLogOn),
                    idleTimeInMinutes: Number(tokenObject.IdleTimeInMinutesStr),
                    logOutTimeOutInMinutes: Number(tokenObject.LogOutTimeOutInMinutesStr),
                    userActiveSessionId: String(tokenObject.UserActiveSessionId),
                    IsCustomer: tokenObject.IsCustomer === "True",
                    CustomerId: String(tokenObject.CustomerId),
                    PermissionScope: String(tokenObject?.PermissionScope),
                };
                state.vehicleNumber = tokenObject.VehicleNumber;
                state.shiftType = Number(tokenObject.ShiftType);
                state.clusterId = String(tokenObject.ClusterId);
                state.vehicleSessionId = tokenObject.VehicleSessionId;
                state.appId = tokenObject.AppId;
                state.hasTransportLocations = tokenObject.HasTransportLocations === "True";
                state.hasAgedCareLocations = tokenObject.HasAgedCareLocations === "True";

                api.setToken(state.token ?? '');
            }
        },

        logout(state: IAuthState) {
            state.token = '';
            state.isAuthenticated = false;
            state.vehicleSessionId = null;
            state.vehicleNumber = null;
            state.shiftType = null;
            state.clusterId = null;
            state.data = null;
            state.hasTransportLocations = false;
            state.hasAgedCareLocations = false;
            window.localStorage.removeItem("persist:transferSchedule");
            window.localStorage.removeItem("persist:PCR");
            window.localStorage.removeItem("persist:transfers");
            window.localStorage.removeItem("recentLocations");
            window.localStorage.removeItem("persist:root");
            window.localStorage.removeItem("i18nextLng");
            window.localStorage.removeItem("specialNeedsMap");
            window.localStorage.removeItem("medicationRoutes");
            window.localStorage.removeItem("specialNeeds");
            window.localStorage.removeItem("customerLocations");
            window.localStorage.removeItem("medicationUnits");
            //window.localStorage.removeItem("mdtwrapper_app_id");
            window.localStorage.removeItem("pageToGoAfterLogin");
            window.localStorage.removeItem("transferAcuity");
            window.localStorage.removeItem("IsOverlapTransferPresent");
            window.localStorage.removeItem("ViewPCR:PageNumber");
            window.localStorage.removeItem("ViewPCR:StartDate");
            window.localStorage.removeItem("ViewPCR:EndDate");
            window.localStorage.removeItem("ViewPCR:Keyword");
            window.localStorage.removeItem("ViewPCR:SortColumn");
            window.localStorage.removeItem("ViewPCR:SortDirection");
            window.localStorage.removeItem("ViewPCR:CurrentStatus");
            window.localStorage.removeItem("CurrentTeamMembers");
            window.localStorage.removeItem("Reqcallout:LocationName");
            window.localStorage.removeItem("Reqcallout:WardName");
            window.localStorage.removeItem("Reqcallout:LocationId");
            window.localStorage.removeItem("Reqcallout:WardId");
            window.localStorage.removeItem("SelectedLocation");
            window.localStorage.removeItem("selectedLocationState");
            window.localStorage.removeItem("selectedLocationsId");
            localStorage.removeItem("Reqcallout:Checkward");
            localStorage.setItem('isLogOut', "true");
            api.setToken(state.token ?? '');
        },
        setDriverLoginToken(state: IAuthState, action: PayloadAction<string>) {
            state.token = action.payload;
            state.isAuthenticated = true;
            state.sessionExpired = false;
            const tokenObject = parseJwt(action.payload ?? '');

            state.data = {
                givenName: String(tokenObject.given_name),
                surName: String(tokenObject.family_name),
                email: String(tokenObject.email),
                role: String(tokenObject.role),
                IsAdmin: tokenObject.IsAdmin === "True",
                IsSMAUser: tokenObject.IsSMAUser === "True",
                exp: tokenObject.exp,
                location: String(tokenObject.location ?? tokenObject.Location),
                ward: String(tokenObject.ward ?? tokenObject.Ward),
                isSharedUser: tokenObject.isSharedUser === "True",
                lastLogin: String(tokenObject.LastLoginDate),
                lastLoginUTC: String(tokenObject.LastLogOn),
                idleTimeInMinutes: Number(tokenObject.IdleTimeInMinutesStr),
                logOutTimeOutInMinutes: Number(tokenObject.LogOutTimeOutInMinutesStr),
                userActiveSessionId: String(tokenObject.UserActiveSessionId),
                IsCustomer: tokenObject.IsCustomer === "True",
                CustomerId: String(tokenObject.CustomerId),
                PermissionScope: String(tokenObject?.PermissionScope),
            };
            state.vehicleNumber = tokenObject.VehicleNumber;
            state.shiftType = Number(tokenObject.ShiftType);
            state.clusterId = String(tokenObject.ClusterId);
            state.vehicleSessionId = tokenObject.VehicleSessionId;
            state.hasTransportLocations = tokenObject.HasTransportLocations === "True";
            state.hasAgedCareLocations = tokenObject.HasAgedCareLocations === "True";

            api.setToken(state.token ?? '');
        },
        setSessionExpired(state: IAuthState) {
            state.sessionExpired = true;
        },
    }
});

export const { setDriverLoginToken } = authSlice.actions;

export const login = (userName: string, password: string): AppThunk<Promise<ILoginResult>> => async (dispatch: AppDispatch) => {
    const res = await api.login("", { Username: userName, Password: password });
    const newAuth: ILoginResult = res;
    dispatch(authSlice.actions.login(newAuth));
    return newAuth;
}
export const loginWithOTP = (userName: string, password: string, token: string): AppThunk<Promise<ILoginResult>> => async (dispatch: AppDispatch) => {
    const res = await api.checkLoginOTP("", { Username: userName, Password: password, Token: token });
    const newAuth: ILoginResult = res;
    dispatch(authSlice.actions.login(newAuth));
    return newAuth;
}
export const loginWithSMSOTP = (userName: string, password: string, token: string): AppThunk<Promise<ILoginResult>> => async (dispatch: AppDispatch) => {
    const res = await api.checkLoginSMS("", { Username: userName, Password: password, Token: token });
    const newAuth: ILoginResult = res;
    dispatch(authSlice.actions.login(newAuth));
    return newAuth;
}
export const logOut = (): AppThunk<Promise<void>> => async (dispatch: AppDispatch) => {
    const res = await api.logout("", null);
    dispatch(authSlice.actions.logout());
}
export const setSessionExpired = (): AppThunk<Promise<void>> => async (dispatch: AppDispatch) => {
    api.setSessionExpired("", null);
    dispatch(authSlice.actions.setSessionExpired());
}
export default authSlice.reducer;